import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";

const faqs: FAQItem[] = [
  {
    question:
      "How do I bring up mental health with my boss without overstepping?",
    answer:
      "Keep it short, specific, and behavioral. Don't name a diagnosis — lead with what you've observed: 'You've canceled eleven external meetings this month.' Don't reference the trauma directly unless they do. Don't suggest a vacation. Say it once, then stop and let the silence sit. Frame it as 'I've noticed, I'm not judging, and if you ever wanted help finding someone to talk to, I'd handle it quietly.' Then go back to work as normal.",
  },
  {
    question: "What if my boss gets angry or defensive when I raise it?",
    answer:
      "Stay calm, apologize for the timing if it helps, and don't argue. Don't retreat from the role; go back to work as normal. The anger usually passes into embarrassment, and then into something more open. People often come back weeks later. Decide in advance that you won't take it personally — that's the illness talking, and it's also a person protecting their dignity.",
  },
  {
    question: "What should I NOT say to my boss about their mental health?",
    answer:
      "Don't name a diagnosis ('I think you're depressed'). Don't reference the trauma directly unless they do. Don't suggest a vacation. Don't compare them to anyone. Don't promise anything you can't deliver. Don't bring up the business impact in this conversation — they know, and making it explicit feels like a performance review. Keep it about them, right now, not the event or the company.",
  },
  {
    question: "What should I have ready when my boss asks what to do?",
    answer:
      "A specific option with a phone number that you've already looked into and can describe in two sentences. For an executive whose depression hasn't responded to medication, SAINT is often the most relevant option: FDA-cleared, five days instead of six weeks, no anesthesia, no drugs, and in trials 79 percent reached remission. You can call Neuro Wellness at 954-827-9793 yourself to understand the process. You can't schedule treatment for them, but you can hand them a name, a number, and a one-line summary.",
  },
];

const related: RelatedLink[] = [
  {
    title:
      "You're Watching Your Boss Fall Apart. Here's What You're Actually Seeing.",
    href: "/watching-your-boss-fall-apart",
    description:
      "The first article in this series: recognizing what you're seeing and understanding the mechanism behind it.",
  },
  {
    title: "What to Have Ready: A Resource Guide for the Executive Assistant",
    href: "/what-to-have-ready-executive-assistant-resource-guide",
    description:
      "The third article: the treatment options, which one fits an executive's life, and how to make the first step happen.",
  },
  {
    title: "The Executive Who Can't Afford Six Weeks",
    href: "/the-executive-who-cant-afford-six-weeks",
    description:
      "Why high performers get stuck in partial treatment — and what changes the calculation.",
  },
  {
    title: "How SAINT® Compares to Other Depression Treatment Options",
    href: "/how-saint-compares-to-other-depression-treatment-options",
    description:
      "A side-by-side comparison of SAINT® against medication, standard TMS, esketamine, and ECT.",
  },
];

export default function HowToTellYourBossYoureWorried() {
  return (
    <DepressionContentPage
      seo={{
        title: "How to Tell Your Boss You're Worried About Them",
        description:
          "For executive assistants: how to raise a mental health concern with your boss without losing your job or their trust. What to say, what not to say, and what to have ready.",
        canonical: "/how-to-tell-your-boss-youre-worried",
        keywords:
          "how to tell boss about mental health, executive assistant raise concern, workplace mental health conversation, depression executive workplace, SAINT TMS, how to talk to boss about depression, executive mental health, what to say worried about boss",
      }}
      badge="For Executive Assistants"
      heroTitle="How to Tell Your Boss You're Worried About Them (Without Losing Your Job or Their Trust)"
      heroSubtitle="This is the hardest part, and it's the reason most executive assistants never say anything. Your entire role is built on discretion. Raising a mental health concern with the person who signs your reviews feels like a violation of every unwritten rule of the job. It's not. But it does need to be done carefully."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                This is the hardest part, and it's the reason most executive
                assistants never say anything.
              </p>
              <p>
                You've seen the pattern. You're fairly sure what it is. And
                you're stuck, because your entire role is built on discretion,
                on not making things about you, on never presuming to know
                what's going on inside the person you support. Raising a mental
                health concern with the person who signs your reviews feels like
                a violation of every unwritten rule of the job.
              </p>
              <p>
                It's not. But it does need to be done carefully. This article is
                about how.
              </p>
            </div>
          ),
        },
        {
          heading: "First: decide whether it's your place",
          body: (
            <div className="space-y-5">
              <p>
                It might not be. If your executive has a spouse, a close friend,
                a chief of staff, or a board member who's already aware and
                engaged, your role may be to support that person's efforts
                rather than to open a separate front.
              </p>
              <p>
                But often there is no such person. Or the people who should be
                seeing it are too far away, too busy, or too deferential. If
                you've looked around and realized that you might be the only one
                who sees the whole picture, then yes, it's your place. Not
                because you're obligated, but because silence has a cost, and
                you're watching it accrue.
              </p>
            </div>
          ),
        },
        {
          heading: "Second: get your own footing",
          body: (
            <div className="space-y-5">
              <p>
                Before you say anything, be clear with yourself about three
                things.
              </p>
              <p>
                <strong>What you've observed.</strong> Specific, behavioral,
                factual. "You've canceled eleven external meetings this month."
                "You've asked me to re-send the same document four times."
                "You've been in the office before six every day for three
                weeks." Not "you seem depressed." You're not qualified to
                diagnose, and leading with a diagnosis invites denial.
              </p>
              <p>
                <strong>What you're asking for.</strong> Not that they confide
                in you. Not that they explain. Only that they consider talking
                to someone qualified, and that they know you've noticed and
                you're not judging.
              </p>
              <p>
                <strong>What you'll do if it goes badly.</strong> They may be
                defensive, embarrassed, or cold. That's the illness talking, and
                it's also a person protecting their dignity. Decide in advance
                that you won't take it personally, won't retreat entirely, and
                won't bring it up again in a way that feels like pressure.
              </p>
            </div>
          ),
        },
        {
          heading: "Third: pick the moment",
          body: (
            <div className="space-y-5">
              <p>
                Not in a meeting. Not by email. Not when they're already in a
                bad state. A quiet moment, in private, at the end of a day that
                wasn't a disaster. If you have a standing one-on-one, that's
                ideal. If you don't, ask for ten minutes.
              </p>
            </div>
          ),
        },
        {
          heading: "Fourth: say it",
          body: (
            <div className="space-y-5">
              <p>Keep it short. Something like:</p>
              <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground">
                "I want to say something, and I want to say it once. I've
                noticed some things over the past couple of months that worry
                me. You've been cancelling meetings you'd never have cancelled
                before. You seem exhausted. You don't seem like yourself. I'm
                not asking you to tell me anything. I just want you to know that
                I've noticed, that I'm not judging, and that if you ever wanted
                help finding someone to talk to, I'd handle it quietly and
                completely. That's all."
              </blockquote>
              <p>
                Then stop. Let the silence sit. Don't fill it with reassurance
                or explanation.
              </p>
            </div>
          ),
        },
        {
          heading: "What not to say",
          body: (
            <div className="space-y-5">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong>Don't name a diagnosis.</strong> "I think you're
                  depressed" invites an argument about the label.
                </li>
                <li>
                  <strong>
                    Don't reference the trauma directly unless they do.
                  </strong>
                  "Ever since the divorce" makes it about the event; they need
                  it to be about now.
                </li>
                <li>
                  <strong>Don't suggest a vacation.</strong> They've heard it,
                  and it implies the problem is fatigue.
                </li>
                <li>
                  <strong>Don't compare them to anyone.</strong> "My uncle went
                  through this" shifts the focus.
                </li>
                <li>
                  <strong>Don't promise anything you can't deliver.</strong>{" "}
                  Discretion, yes. A fix, no.
                </li>
                <li>
                  <strong>
                    Don't bring up the business impact in this conversation.
                  </strong>{" "}
                  They know. Making it explicit feels like a performance review.
                </li>
              </ul>
            </div>
          ),
        },
        {
          heading: "What might happen",
          body: (
            <div className="space-y-5">
              <p>
                <strong>They shut it down.</strong> "I'm fine. Thanks for your
                concern." You say, "Understood. The offer stands," and you let
                it go. You've planted something. People often come back weeks
                later.
              </p>
              <p>
                <strong>They get angry.</strong> Rare, but possible. Stay calm,
                apologize for the timing if it helps, and don't argue. Don't
                retreat from the role; go back to work as normal. The anger
                usually passes into embarrassment, and then into something more
                open.
              </p>
              <p>
                <strong>They break.</strong> More common than you'd think. Some
                people have been waiting for someone to say it. If this happens,
                your job is to listen, not to solve. When they've said what they
                need to say, that's when you offer the next step.
              </p>
              <p>
                <strong>They ask what you'd suggest.</strong> This is the
                outcome you're preparing for, and it's why the third article in
                this series exists. Have an answer ready.
              </p>
            </div>
          ),
        },
        {
          heading: "What to have ready",
          body: (
            <div className="space-y-5">
              <p>
                When they ask "what would I even do," you need something
                concrete and low-friction. Not "you should see someone." A
                specific option, with a phone number, that you've already looked
                into and can describe in two sentences.
              </p>
              <p>
                For someone who has been through trauma and is now caught in a
                depressive spiral, especially someone who may already have tried
                medication or therapy and is still stuck, one of the most
                relevant options is SAINT, an FDA-cleared, accelerated form of
                transcranial magnetic stimulation developed at Stanford. It's a
                five-day treatment, not a six-week one, and in clinical trials
                it produced remission in 79 percent of people whose depression
                hadn't responded to medication, with an average time to
                remission of 2.6 days. It's delivered at{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Providers
                </Link>
                , including Neuro Wellness TMS Centers of America, which offers
                private outpatient care with weekend availability.
              </p>
              <p>
                You can call them yourself, in advance, to understand the
                process well enough to explain it. You can't schedule treatment
                on someone's behalf, and you shouldn't. But you can hand your
                executive a name, a number, and a one-line summary, and say, "I
                looked into this. If you want, I'll set up the first call and
                put it on the calendar as whatever you want it called."
              </p>
              <p>
                That's the whole job. See it, say it once, have the answer
                ready.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p className="font-semibold text-primary">
                Ask questions or schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If your executive says anything that suggests they're thinking
                about ending their life, treat it as an emergency. Encourage
                them to call or text 988 (Suicide &amp; Crisis Lifeline), or
                call it yourself for guidance on how to help. This article is
                for educational purposes and is not a substitute for individual
                medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
